import type { HTMLAttributes, ReactElement, ReactNode } from 'react';
import { PixelIcon, type PixelIconName } from '../PixelIcon.js';
import { Screen } from './Screen.js';

export type StatusScreenVariant = 'error' | 'loading' | 'reconnecting' | 'info';

/** The variant's pixel icon (design system 02 §2.8); loading shows the loader coins. */
const STATUS_ICONS: Record<Exclude<StatusScreenVariant, 'loading'>, PixelIconName> = {
  error: 'prohibited',
  reconnecting: 'signal',
  info: 'effect',
};

export interface StatusScreenProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  variant: StatusScreenVariant;
  heading?: ReactNode;
  message?: ReactNode;
  action?: ReactNode;
  panelSize?: 'md' | 'compact';
  panelClassName?: string;
}

export function StatusScreen({
  variant,
  heading,
  message,
  action,
  panelSize = 'md',
  className,
  panelClassName,
  children,
  ...screenProps
}: StatusScreenProps): ReactElement {
  const screenClassName = ['cards-status', 'cards-status-shell', className]
    .filter(Boolean)
    .join(' ');
  const resolvedPanelClassName = [
    'cards-kit-status-panel',
    `cards-kit-status-panel--${variant}`,
    `cards-kit-status-panel--${panelSize}`,
    action === undefined ? undefined : 'cards-kit-status-panel--with-action',
    panelClassName,
  ].filter(Boolean).join(' ');

  return (
    <Screen {...screenProps} className={screenClassName}>
      <div className={resolvedPanelClassName}>
        <span className="cards-kit-status-icon" aria-hidden="true">
          {variant === 'loading' ? (
            <span className="cards-delayed-loader-mark"><span /><span /><span /></span>
          ) : (
            <PixelIcon name={STATUS_ICONS[variant]} />
          )}
        </span>
        {heading === undefined ? null : (
          <strong className="cards-kit-status-heading">{heading}</strong>
        )}
        {message === undefined ? null : (
          <p className="cards-kit-status-message">{message}</p>
        )}
        {children}
        {action}
      </div>
    </Screen>
  );
}
